import React from "react";

const findStartNode = (scrollTop, nodePositions, count) => {
    let startRange = 0;
    let endRange = count === 0 ? 0 : count - 1;
    while (endRange !== startRange) {
        const middle = Math.floor((endRange - startRange) / 2 + startRange);
        if (
            nodePositions[middle].top <= scrollTop
            && nodePositions[middle + 1].top > scrollTop
        ) {
            return middle;
        }

        if (middle === startRange) {
            return endRange;
        }
        if (nodePositions[middle].top <= scrollTop) {
            startRange = middle;
        } else {
            endRange = middle;
        }
    }
    return count;
};

const findEndNode = (nodePositions, startNode, count, height) => {
    let endNode;
    for (endNode = startNode; endNode < count; endNode++) {
        if (nodePositions[endNode].top > nodePositions[startNode].top + height) {
            return endNode;
        }
    }
    return endNode;
};
const getHeight = (row, top, getCustomHeight, customHeight) => {
    if (getCustomHeight) {
        return top + getCustomHeight(row);
    }
    if (customHeight) {
        return top + customHeight;
    }
    return top + 20.2;
};
const useScrollAware = (load, page, count, maxCount, rows) => {
    const [scrollTop, setScrollTop] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false);

    const ref = React.useRef();
    const refHasScrolled = React.useRef(false);
    const animationFrame = React.useRef();

    const onScroll = React.useCallback(({ target }) => {
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current);
        }
        animationFrame.current = requestAnimationFrame(() => {
            setScrollTop(target.scrollTop);
        });
    }, []);
    const loadOptions = React.useCallback(async (e) => {
        const { target } = e;
        if (e.type === "scroll" && !refHasScrolled.current) {
            refHasScrolled.current = true;
        }
        if (load && (target.scrollHeight - target.clientHeight) * 0.75 <= target.scrollTop && count > maxCount && !isLoading) {
            setIsLoading(true);
            await load();
        }
    }, [page, count, isLoading]);

    React.useEffect(() => {
        if (page === 0) {
            setScrollTop(0);
            ref.current.scrollTop = 0;
        }
        setIsLoading(false);
    }, [page, rows]);

    React.useEffect(() => {
        const scrollContainer = ref.current;
        scrollContainer.addEventListener("scroll", onScroll);
        return () => {
            scrollContainer.removeEventListener("scroll", onScroll);
        };
    }, []);

    // load more options if limit is to small for screen size
    React.useEffect(() => {
        if (!refHasScrolled.current) {
            loadOptions({ target: ref.current });
        }
    }, [rows]);

    React.useEffect(() => {
        const scrollContainer = ref.current;
        scrollContainer.addEventListener("scroll", loadOptions);
        return () => {
            scrollContainer.removeEventListener("scroll", loadOptions);
        };
    }, [page, count, isLoading]);
    return [scrollTop, ref];
};

function VirtualScroll({
    Row,
    height,
    rows,
    options,
    disabledActions,
    element,
    clickHandlersRefresher,
    ...other
}) {
    const {
        load,
        page = 0,
        count,
        customHeight,
        getCustomHeight,
        renderAhead = 20,
        CustomRow,
    } = options.virtualization;

    const [visibleChildren, setVisibleChildren] = React.useState(null);

    const [scrollTop, ref] = useScrollAware(load, page, count, rows.length, rows);

    const firstVisibleNode = React.useMemo(
        () => findStartNode(scrollTop, rows, rows.length),
        [scrollTop, rows],
    );

    const lastVisibleNode = React.useMemo(
        () => findEndNode(rows, firstVisibleNode, rows.length, height),
        [firstVisibleNode, rows, height],
    );

    const totalHeight = React.useMemo(() => (rows.length > 0 ? getHeight(rows.at(-1), rows.at(-1).top, getCustomHeight, customHeight) : 0), [rows, count]);
    const startNode = Math.max(0, firstVisibleNode - renderAhead);
    const endNode = Math.min((0 || rows.length - 1), lastVisibleNode + renderAhead);
    const visibleNodeCount = endNode - startNode + 1;
    React.useEffect(() => {
        if (rows.length !== 0 && visibleNodeCount > 0) {
            setVisibleChildren(new Array(visibleNodeCount).fill(null)
                .map((_, index) => (
                    CustomRow ? (
                        <CustomRow
                            top={rows[index + startNode].top}
                            key={rows[index + startNode].id}
                            row={rows[index + startNode]}
                            options={options}
                            disabledActions={disabledActions}
                            {...other}
                        />
                    ) : (
                        <Row
                            top={rows[index + startNode].top}
                            key={rows[index + startNode].id}
                            row={rows[index + startNode]}
                            options={options}
                            disabledActions={disabledActions}
                            i={index + startNode}
                            {...other}
                        />
                    )
                )));
        } else {
            setVisibleChildren(null);
        }
    }, [startNode, visibleNodeCount, options, disabledActions, clickHandlersRefresher]);

    return (
        <div
            style={{
                height: height - 20.2,
                overflow: "auto",
                width: "100%",
            }}
            ref={ref}
        >
            <div
                style={{
                    overflow: "hidden",
                    height: totalHeight,
                    position: "relative",
                    width: "100%",
                    display: "grid",
                }}
                ref={element}
            >
                {visibleChildren}
            </div>
        </div>
    );
}

export default React.memo(VirtualScroll);
